const { isURL, isBase64 } = require('validator');

const POSITION_INLINE = 'inline';
const POSITION_LEFT = 'left';
const POSITION_CENTER = 'center';
const POSITION_RIGHT = 'right';

const SIZE_SMALL = '25%';
const SIZE_MEDIUM = '50%';
const SIZE_LARGE = '75%';
const SIZE_FULL = '100%';
const SIZE_ORIGINAL = 'auto';

const IMAGE_MARGIN = '25px';

const IMAGE_KEY = 'image';

const VALID_POSITIONS = [POSITION_INLINE, POSITION_LEFT, POSITION_CENTER, POSITION_RIGHT];
const VALID_SIZES = [SIZE_SMALL, SIZE_MEDIUM, SIZE_LARGE, SIZE_FULL, SIZE_ORIGINAL];

const sanitizeImageNode = (node) => {

	const { src, alt, title, position, size } = node.attrs;

	// Check to make sure that the src is valid for the image...
	if (src &&
		!isURL(src, { protocols: ['http', 'https'] }) &&
		!isBase64(src, { urlSafe: true })
	) {
		throw new Error(`Invalid "src" attribute for image node: ${src}`);
	}

	let safePosition = POSITION_INLINE;
	let safeSize = SIZE_MEDIUM;

	// Check to make sure that the position is valid...
	if (VALID_POSITIONS.indexOf(position) >= 0) {
		safePosition = position;
	}

	// Check to make sure that the size is valid...
	if (VALID_SIZES.indexOf(size) >= 0) {
		safeSize = size;
	}

	const safeAttrs = {
		src,
		position: safePosition,
		size: safeSize,
	};

	if (alt) {
		safeAttrs.alt = alt;
	}

	if (title) {
		safeAttrs.title = title;
	}

	node.attrs = safeAttrs;

	return node;

};

const ImageNodeSpec = {
	inline: true,
	attrs: {
		src: {},
		alt: {
			default: '',
		},
		title: {
			default: '',
		},
		size: {
			default: SIZE_MEDIUM,
		},
		position: {
			default: POSITION_INLINE,
		},
	},
	group: 'inline',
	parseDOM: [{
		tag: 'img[src]',
		getAttrs(dom) {

			// Position options: float-right, float-left, mx-auto, img-fluid
			// Size options: w-25, w-50, w-75, w-100
			let position = POSITION_INLINE, size = SIZE_MEDIUM;

			if (dom.getAttribute('class')) {

				const classNames = dom.getAttribute('class').split(' ');

				// Parse position from class name
				if (classNames.includes('mx-auto')) {

					position = POSITION_CENTER;

				} else if (classNames.includes('float-left')) {

					position = POSITION_LEFT;

				} else if (classNames.includes('float-right')) {

					position = POSITION_RIGHT;

				}

				// Parse size from class name
				if (classNames.includes('w-25')) {

					size = SIZE_SMALL;

				} else if (classNames.includes('w-50')) {

					size = SIZE_MEDIUM;

				} else if (classNames.includes('w-75')) {

					size = SIZE_LARGE;

				} else if (classNames.includes('w-100')) {

					size = SIZE_FULL;

				// Default to original size if no size is defined
				} else {

					size = SIZE_ORIGINAL;

				}

			}

			return {
				src: dom.getAttribute('src'),
				alt: dom.getAttribute('alt') || '',
				title: dom.getAttribute('title') || '',
				position,
				size,
			};

		},
	}],
	toDOM(node) {

		const safeNode = sanitizeImageNode(node);

		const { src, alt, title, size, position } = safeNode.attrs;

		let style = '';

		if (position === POSITION_INLINE) {

			style += 'display: inline;';

		} else if (position === POSITION_CENTER) {

			style += 'display: block;';
			style += ` margin: ${IMAGE_MARGIN} auto; `;

		} else if (position === POSITION_LEFT || position === POSITION_RIGHT) {

			style += ` float: ${position};`;

			let margin;

			if (position === POSITION_LEFT) {

				margin = `${IMAGE_MARGIN} ${IMAGE_MARGIN} ${IMAGE_MARGIN} 0px`;

			} else {

				margin = `${IMAGE_MARGIN} 0px ${IMAGE_MARGIN} ${IMAGE_MARGIN}`;

			}

			style += ` margin: ${margin};`;

		}

		style += ` width: ${size}; max-width: 100%;`;

		return ['img', { src, alt, title, style }];

	},

};

module.exports = {
	ImageNodeSpec,
	IMAGE_KEY,
	POSITION_INLINE,
	POSITION_LEFT,
	POSITION_CENTER,
	POSITION_RIGHT,
	SIZE_SMALL,
	SIZE_MEDIUM,
	SIZE_LARGE,
	SIZE_FULL,
	SIZE_ORIGINAL,
	IMAGE_MARGIN,
	sanitizeImageNode,
};
